import React, { useState, useEffect, useCallback } from 'react';
import useGeocoding from '../../hooks/useGeocoding';

interface ControlPanelProps {
  center: { lat: number; lng: number };
  onCenterChange: (center: { lat: number; lng: number }) => void;
  address: string;
  onAddressSearch: (address: string) => void;
}

const ControlPanel: React.FC<ControlPanelProps> = ({ 
  center, 
  onCenterChange,
  address,
  onAddressSearch
}) => {
  const [lat, setLat] = useState<string>(center.lat.toString());
  const [lng, setLng] = useState<string>(center.lng.toString());
  const [searchAddress, setSearchAddress] = useState<string>(address);
  const [error, setError] = useState<string>('');

  const { searchAddress: geocodeAddress, loading, error: geocodeError } = useGeocoding();

  // Update local state when center prop changes
  useEffect(() => {
    setLat(center.lat.toFixed(6));
    setLng(center.lng.toFixed(6));
    // Clear any error messages when coordinates are updated externally (via dragging)
    setError('');
  }, [center]);

  // Validate coordinates
  const validateCoordinates = (lat: string, lng: string): boolean => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    
    if (isNaN(latNum) || isNaN(lngNum)) {
      setError('Coordinates must be numbers');
      return false;
    }
    
    if (latNum < -90 || latNum > 90) {
      setError('Latitude must be between -90 and 90');
      return false;
    }
    
    if (lngNum < -180 || lngNum > 180) {
      setError('Longitude must be between -180 and 180');
      return false;
    }
    
    setError('');
    return true;
  };

  // Handle coordinate change
  const handleCoordinateChange = () => {
    if (validateCoordinates(lat, lng)) {
      onCenterChange({
        lat: parseFloat(lat),
        lng: parseFloat(lng)
      });
    }
  };

  // Handle address search
  const handleAddressSearch = useCallback(async () => {
    if (!searchAddress.trim()) {
      setError('Please enter an address to search');
      return;
    }
    
    try {
      const result = await geocodeAddress(searchAddress);
      if (result) {
        onCenterChange(result);
        onAddressSearch(searchAddress);
        setError('');
      }
    } catch (error) {
      setError('Failed to geocode address. Please try again.');
    }
  }, [searchAddress, geocodeAddress, onCenterChange, onAddressSearch]);

  // Handle input keydown for Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddressSearch();
    }
  };

  return (
    <div className="control-panel">
      <div className="input-container">
        <div className="address-search">
          <div className="search-container">
            <input
              id="address-input"
              type="text"
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter address or location"
              aria-label="Search address"
            />
            <button 
              onClick={handleAddressSearch} 
              disabled={loading}
              aria-label="Search"
              title="Search for address"
            >
              🔍
            </button>
          </div>
        </div>
        
        <div className="coordinate-inputs">
          <div className="coordinate-input">
            <input
              id="lat-input"
              type="text"
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              onBlur={handleCoordinateChange}
              onKeyDown={(e) => e.key === 'Enter' && handleCoordinateChange()}
              placeholder="Latitude"
              aria-label="Latitude coordinate"
            />
          </div>
          
          <div className="coordinate-input">
            <input
              id="lng-input"
              type="text"
              value={lng}
              onChange={(e) => setLng(e.target.value)}
              onBlur={handleCoordinateChange}
              onKeyDown={(e) => e.key === 'Enter' && handleCoordinateChange()}
              placeholder="Longitude"
              aria-label="Longitude coordinate"
            />
          </div>
        </div>
      </div>
      
      {(error || geocodeError) && (
        <div className="error-message" role="alert">
          {error || geocodeError}
        </div>
      )}
      
      {loading && <div className="loading-indicator">Searching...</div>}
    </div>
  );
};

export default ControlPanel;
